import { getSizesWithNeighbors } from '~/shared/utils/getSizesWithNeighbors';
import {
  GENDER_FILTER_FEMALE,
  GENDER_FILTER_MALE,
} from '~/shared/utils/useGender';

export const BUILDER_AUTO_SIZE_FILTER_TYPES = {
  DISABLED: 'disable-automatic-size-filtering',
  PROFILE_SIZE_ONLY: 'profile-size-only',
  PROFILE_SIZE_WITH_UP_DOWN: 'profile-size-with-up-down',
};

/**
 *
 * @param {string} gridGender "F" | "M"
 * @param {string} userGender "F" | "M"
 * @param {Object} profileSizes the user's preferred sizes, e.g. { "size_bottom": "Medium", "size_bra": "Medium", "size_top": "Medium", "size_one_piece": "Medium"}
 * @param {string} automaticSizeFilteringType one of the BUILDER_AUTO_SIZE_FILTER_TYPES
 * @param {boolean} automaticGenderFiltering if gender filter should be auto applied
 * @returns {Object} The autofilter params to apply
 *
 * ===== for automaticSizeFilteringType =====
 * Example for PROFILE_SIZE_WITH_UP_DOWN:
 * {
 *   size_bottom: ["Small", "Medium", "Large"],
 *   size_bra: ["Small", "Medium", "Large"],
 *   size_top: ["Small", "Medium", "Large"],
 *   size_one_piece: ["Small", "Medium", "Large"],
 * }
 *
 * Example for PROFILE_SIZE_ONLY:
 * {
 *   size_bottom: "Medium",
 *   size_bra: "Medium",
 *   size_top: "Medium",
 *   size_one_piece: "Medium",
 * }
 * =========================================
 */
export const getAutoFilterParams = ({
  gridGender,
  userGender,
  profileSizes,
  automaticSizeFilteringType,
  automaticGenderFiltering,
}) => {
  const autoSizeFilterParams = getAutoSizeFilterParams({
    gridGender,
    userGender,
    profileSizes,
    automaticSizeFilteringType,
  });
  const autoGenderFilterParam = getAutoGenderFilterParam(
    automaticGenderFiltering,
    userGender
  );

  return {
    ...autoSizeFilterParams,
    ...autoGenderFilterParam,
  };
};

const getAutoSizeFilterParams = ({
  gridGender,
  userGender,
  profileSizes,
  automaticSizeFilteringType,
}) => {
  if (
    !automaticSizeFilteringType ||
    !profileSizes ||
    !gridGender ||
    !userGender
  ) {
    return {};
  }

  if (gridGender !== userGender) {
    return {};
  }

  switch (automaticSizeFilteringType) {
    case BUILDER_AUTO_SIZE_FILTER_TYPES.DISABLED:
      return {};
    case BUILDER_AUTO_SIZE_FILTER_TYPES.PROFILE_SIZE_ONLY:
      return profileSizes;
    case BUILDER_AUTO_SIZE_FILTER_TYPES.PROFILE_SIZE_WITH_UP_DOWN:
      return Object.entries(profileSizes).reduce((acc, [key, value]) => {
        acc[key] = getSizesWithNeighbors({ size: value, gender: userGender });
        return acc;
      }, {});
    default:
      return {};
  }
};

const getAutoGenderFilterParam = (automaticGenderFiltering, userGender) => {
  if (!automaticGenderFiltering || !userGender) {
    return {};
  }

  return {
    tag_gender_list:
      userGender === 'F' ? GENDER_FILTER_FEMALE : GENDER_FILTER_MALE,
  };
};
